/**
 * Global loading-indicator store (CLAUDE.md §18.6).
 *
 * ONE shared mechanism for every async operation app-wide — data retrieve,
 * save, validate, delete, export, report generation, any API call.  The shared
 * axios client (`api/client.ts`) begins/ends a loading op around EVERY request
 * automatically, so developers never hand-add a spinner per call.  Components
 * read the state through `useLoading()` / `<GlobalLoadingOverlay>`; non-React
 * code (the axios interceptor, `useSaveFlow`) drives it through the module-level
 * `loadingBegin/End/runLoading` helpers below.
 *
 * The rendered SYMBOL is client-configurable (per tenant) — `AI_AppSettings`
 * key `LoaderSymbol`, surfaced via GET /api/v1/app-config and applied after
 * login with `setLoaderSymbol(...)`.  Colours come from the `--ai-loader-*`
 * theme tokens (see loading.css) — never hardcoded here.
 */
import { create } from 'zustand';

/** The selectable loading symbols (client-configurable). */
export const LOADER_SYMBOLS = ['ring', 'dots', 'bars', 'pulse', 'dual-ring', 'orbit'] as const;
/** One of the selectable symbol keys. (Named *Key to avoid colliding with the
 *  <LoaderSymbol> component re-exported from the same barrel.) */
export type LoaderSymbolKey = (typeof LOADER_SYMBOLS)[number];

/** Shipped when a client hasn't chosen one. */
export const DEFAULT_LOADER_SYMBOL: LoaderSymbolKey = 'ring';

/** Narrowing guard — used by the config-apply path and the API validator twin. */
export function isLoaderSymbol(v: unknown): v is LoaderSymbolKey {
  return typeof v === 'string' && (LOADER_SYMBOLS as readonly string[]).includes(v);
}

/** Default loader colour (brand orange) — overridable per client. */
export const DEFAULT_LOADER_COLOR = '#e8670a';

/** True for a #RGB or #RRGGBB hex colour. */
export function isLoaderColor(v: unknown): v is string {
  return typeof v === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v.trim());
}

/** Selectable progress-bar styles (client-configurable) — applies to every
 *  progress bar app-wide (top bar, scrim bar, report-generation bar). */
export const PROGRESS_BAR_STYLES = ['solid', 'stripes', 'pulse', 'glow'] as const;
export type ProgressBarStyle = (typeof PROGRESS_BAR_STYLES)[number];
export const DEFAULT_PROGRESS_STYLE: ProgressBarStyle = 'solid';
export const DEFAULT_PROGRESS_COLOR = '#e8670a';

export function isProgressStyle(v: unknown): v is ProgressBarStyle {
  return typeof v === 'string' && (PROGRESS_BAR_STYLES as readonly string[]).includes(v);
}

interface LoadingState {
  /** Count of in-flight tracked ops — drives the top progress bar. */
  active: number;
  /** Count of in-flight BLOCKING ops — drives the full-screen scrim (prevents
   *  double-submit on save / validate / delete / export / report). */
  blocking: number;
  /** 0..100 when a real progress value exists (export download, report gen);
   *  null = indeterminate (most fetches/saves → animated symbol / slide bar). */
  progress: number | null;
  /** Optional label shown in the blocking overlay (e.g. "Saving…"). */
  label: string | null;
  /** The client-configured symbol applied everywhere the loader renders. */
  symbol: LoaderSymbolKey;
  /** The client-configured loader colour (hex) — mirrors the --ai-loader-color
   *  CSS token so the picker UI can reflect the current value. */
  color: string;
  /** The client-configured progress-bar style — mirrors the
   *  `data-progress-style` attribute that the CSS keys off. */
  progressStyle: ProgressBarStyle;
  /** The client-configured progress-bar colour (hex) — mirrors --ai-progress-color. */
  progressColor: string;
}

export const useLoadingStore = create<LoadingState>(() => ({
  active: 0,
  blocking: 0,
  progress: null,
  label: null,
  symbol: DEFAULT_LOADER_SYMBOL,
  color: DEFAULT_LOADER_COLOR,
  progressStyle: DEFAULT_PROGRESS_STYLE,
  progressColor: DEFAULT_PROGRESS_COLOR,
}));

interface BeginOptions {
  /** Show the blocking scrim (app-level ops). Default false = top-bar only. */
  blocking?: boolean;
  /** Label for the blocking overlay. */
  label?: string;
}

let nextId = 0;
const handles = new Map<number, { blocking: boolean }>();

/** Begin one tracked async op. Returns a handle id to pass to {@link loadingEnd}. */
export function loadingBegin(opts?: BeginOptions): number {
  const id = ++nextId;
  const blocking = opts?.blocking === true;
  handles.set(id, { blocking });
  const s = useLoadingStore.getState();
  useLoadingStore.setState({
    active: s.active + 1,
    blocking: s.blocking + (blocking ? 1 : 0),
    label: opts?.label ?? s.label,
  });
  return id;
}

/** End a tracked op. Resets progress/label once nothing is in flight. */
export function loadingEnd(id: number): void {
  const h = handles.get(id);
  if (!h) return;
  handles.delete(id);
  const s = useLoadingStore.getState();
  const active = Math.max(0, s.active - 1);
  const blocking = Math.max(0, s.blocking - (h.blocking ? 1 : 0));
  useLoadingStore.setState({
    active,
    blocking,
    progress: active === 0 ? null : s.progress,
    label: active === 0 ? null : s.label,
  });
}

/** Set a determinate progress value (0..100), or null for indeterminate. */
export function setLoadingProgress(p: number | null): void {
  useLoadingStore.setState({
    progress: p === null ? null : Math.max(0, Math.min(100, Math.round(p))),
  });
}

/** Apply the client-configured symbol (called after GET /api/v1/app-config).
 *  Ignores unknown values so a future/garbage config never breaks the loader. */
export function setLoaderSymbol(sym: unknown): void {
  if (isLoaderSymbol(sym)) useLoadingStore.setState({ symbol: sym });
}

/** Apply the client-configured loader colour (after GET /api/v1/app-config and
 *  on a live pick).  Sets the shared `--ai-loader-color` CSS token so every
 *  symbol, progress bar and scrim recolours at once.  Ignores invalid values. */
export function setLoaderColor(color: unknown): void {
  if (!isLoaderColor(color)) return;
  const c = (color as string).trim();
  useLoadingStore.setState({ color: c });
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--ai-loader-color', c);
  }
}

/** Apply the client-configured progress-bar STYLE app-wide.  Sets the
 *  `data-progress-style` attribute on <html> so the CSS restyles every progress
 *  bar (top bar, scrim bar, report bar) at once.  Ignores invalid values. */
export function setProgressStyle(style: unknown): void {
  if (!isProgressStyle(style)) return;
  useLoadingStore.setState({ progressStyle: style });
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-progress-style', style);
  }
}

/** Apply the client-configured progress-bar COLOUR app-wide.  Drives the shared
 *  `--ai-progress-color` CSS token so every progress bar recolours at once. */
export function setProgressColor(color: unknown): void {
  if (!isLoaderColor(color)) return;
  const c = (color as string).trim();
  useLoadingStore.setState({ progressColor: c });
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--ai-progress-color', c);
  }
}

/** Wrap any promise so it shows the loader for its lifetime (begin → finally end).
 *  Use for non-API async too (e.g. building an export file client-side). */
export async function runLoading<T>(p: Promise<T>, opts?: BeginOptions): Promise<T> {
  const id = loadingBegin(opts);
  try {
    return await p;
  } finally {
    loadingEnd(id);
  }
}
